import React, { useState, useEffect } from 'react';
import {
  BookOpen, ChevronRight, ChevronLeft, X, Lightbulb, Target, Shield,
  Terminal, Camera, Lock, Zap, Brain, Rocket, Play, CheckCircle,
  HelpCircle, Skull, AlertTriangle, Network, Eye, ArrowRight,
  Sparkles, MessageSquare, Bot, Server, Clock, Activity
} from 'lucide-react';

// Tutorial steps for Sessions page
const tutorialSteps = [
  {
    id: 'welcome',
    title: 'Bienvenue dans les Sessions d\'Exploitation',
    icon: Skull,
    content: `This page lets you manage your access sessions to compromised systems.

A "session" represents an active connection to a target system after successful exploitation. It is your entry point for post-exploitation actions.`,
    tips: [
      'Les sessions actives sont en vert',
      'Closed sessions are shown in grey',
      'Each session can be controlled independently'
    ],
    highlight: 'stats'
  },
  {
    id: 'stats',
    title: 'Comprendre les Statistiques',
    icon: Activity,
    content: `Les cartes de statistiques vous donnent une vue d'ensemble:

- **Total Sessions**: Total number of sessions created
- **Active**: Currently connected sessions
- **Closed**: Terminated or disconnected sessions
- **Persistent**: Sessions with persistence mechanism
- **Privileged**: Sessions with root/admin rights
- **Compromised Hosts**: Number of unique machines`,
    tips: [
      'A privileged session has more capabilities',
      'Persistence allows returning even after a reboot'
    ],
    highlight: 'stats-cards'
  },
  {
    id: 'ai-assistant',
    title: 'Assistant IA - Votre Guide',
    icon: Brain,
    content: `The AI Assistant helps you analyze and plan your actions:

1. Click "AI Assistant" in the top right
2. Utilisez les boutons rapides ou posez vos questions
3. L'IA analysera vos sessions et donnera des recommandations

L'IA peut vous aider pour:
- Choisir les meilleures techniques de privesc
- Plan lateral movements
- Recommander des actions de post-exploitation`,
    tips: [
      'Ask "Which sessions should I prioritize?"',
      'Ask "How to escalate privileges on Linux?"',
      'The AI knows the context of your sessions'
    ],
    highlight: 'ai-button'
  },
  {
    id: 'poc-auto',
    title: 'PoC Auto - Simplified Exploitation',
    icon: Rocket,
    content: `The PoC Auto module lets you automatically exploit vulnerabilities:

1. Click "PoC Auto" to open the panel
2. Select the vulnerabilities to exploit
3. Click "Run PoC" to start exploitation

If exploitation succeeds, a new session will be created automatically.`,
    tips: [
      'Select multiple vulnerabilities at once',
      'Results appear in real time',
      'You can copy the generated PoC code'
    ],
    highlight: 'poc-button'
  },
  {
    id: 'session-actions',
    title: 'Actions sur les Sessions',
    icon: Zap,
    content: `Chaque session active offre plusieurs actions:

- **Screenshot**: Captures the screen of the target system
- **Privilege Escalation**: Tente d'obtenir les droits root/admin
- **Persistence**: Installs a mechanism to return later
- **Terminal**: Ouvre un shell interactif

Click on a session to see more details and options.`,
    tips: [
      'Action icons are on the right of each session',
      'Disabled actions are greyed out',
      'ROOT = elevated privileges, PERSISTENT = permanent access'
    ],
    highlight: 'session-list'
  },
  {
    id: 'apt-modules',
    title: 'Advanced APT Modules',
    icon: Shield,
    content: `APT (Advanced Persistent Threat) modules simulate sophisticated attack techniques:

- Each module corresponds to MITRE ATT&CK techniques
- Select a module to see its details
- Execute it on your active sessions

These modules are useful for testing system resilience.`,
    tips: [
      'MITRE techniques are shown in red badges',
      'Some modules require elevated privileges',
      'Results are visible in the execution log'
    ],
    highlight: 'apt-modules'
  },
  {
    id: 'workflow',
    title: 'Penetration Workflow',
    icon: Network,
    content: `Le diagramme de workflow en bas de page montre le processus complet:

1. **Reconnaissance**: Collecte d'informations
2. **Scanning**: Vulnerability detection
3. **Exploitation**: Session creation
4. **Post-Exploitation**: Actions sur les sessions
5. **Report**: Documentation of results

You are currently in the "Post-Exploitation" phase.`,
    tips: [
      'Follow the workflow for a structured methodology',
      'Chaque phase a ses propres outils',
      'Le rapport final documente tout le processus'
    ],
    highlight: 'workflow'
  }
];

// Quick help topics
const quickHelpTopics = [
  {
    question: 'How to create a new session?',
    answer: 'Sessions are created automatically when a vulnerability is successfully exploited. Use the "PoC Auto" module or launch a scan with exploitation enabled.',
    icon: Terminal
  },
  {
    question: 'What to do if a session is closed?',
    answer: 'A closed session can no longer be used. If you installed persistence, you can try to reconnect. Otherwise, you will need to re-exploit the vulnerability.',
    icon: AlertTriangle
  },
  {
    question: 'How to escalate privileges?',
    answer: 'Click the shield icon next to an active session. The system will automatically try several privilege escalation techniques.',
    icon: Shield
  },
  {
    question: 'What is persistence for?',
    answer: 'Persistence installs a mechanism that allows returning to the system even after a reboot. This is useful for maintaining access over time.',
    icon: Lock
  },
  {
    question: 'Comment utiliser le terminal interactif?',
    answer: 'Click the Terminal icon or a session to open the interactive shell. You can run commands directly on the target system.',
    icon: Terminal
  },
  {
    question: 'Que signifient les badges ROOT et PERSISTENT?',
    answer: 'ROOT indicates the session has administrator privileges. PERSISTENT indicates a persistence mechanism is installed to maintain access.',
    icon: Skull
  }
];

// Main Guide Component
const SessionsGuide = ({
  isOpen,
  onClose,
  onStartTutorial,
  showWelcome = false,
  onDismissWelcome
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showQuickHelp, setShowQuickHelp] = useState(false);
  const [expandedHelp, setExpandedHelp] = useState(null);

  // Tutorial navigation
  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  // Current step data
  const step = tutorialSteps[currentStep];
  const StepIcon = step?.icon || BookOpen;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-black-light rounded-2xl border border-brand-green/30 w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20 bg-gradient-to-r from-brand-green/10 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Guide des Sessions</h2>
                <p className="text-gray-400 text-sm">Learn to use exploitation sessions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowQuickHelp(!showQuickHelp)}
                className={`p-2 rounded-lg transition-colors ${
                  showQuickHelp
                    ? 'bg-brand-green text-brand-black'
                    : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
                }`}
                title="Aide rapide"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {showQuickHelp ? (
            /* Quick Help View */
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-brand-green" />
                Frequently Asked Questions
              </h3>
              <div className="space-y-3">
                {quickHelpTopics.map((topic, idx) => {
                  const TopicIcon = topic.icon;
                  return (
                    <div
                      key={idx}
                      className="bg-brand-black rounded-xl border border-gray-700 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedHelp(expandedHelp === idx ? null : idx)}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-brand-black-lighter transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <TopicIcon className="w-5 h-5 text-brand-green" />
                          <span className="text-white font-medium">{topic.question}</span>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedHelp === idx ? 'rotate-90' : ''
                        }`} />
                      </button>
                      {expandedHelp === idx && (
                        <div className="p-4 pt-0 border-t border-gray-700">
                          <p className="text-gray-300 text-sm leading-relaxed">{topic.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Tutorial View */
            <div className="space-y-6">
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2">
                {tutorialSteps.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => goToStep(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentStep
                        ? 'bg-brand-green w-6'
                        : idx < currentStep
                        ? 'bg-brand-green/50'
                        : 'bg-gray-600'
                    }`}
                    title={s.title}
                  />
                ))}
              </div>

              {/* Current step content */}
              <div className="bg-brand-black rounded-xl border border-gray-700 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                    <StepIcon className="w-7 h-7 text-brand-green" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-brand-green font-mono">
                        Step {currentStep + 1}/{tutorialSteps.length}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{step.title}</h3>
                  </div>
                </div>

                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 whitespace-pre-line leading-relaxed">
                    {step.content}
                  </p>
                </div>

                {/* Tips */}
                {step.tips && step.tips.length > 0 && (
                  <div className="mt-6 bg-brand-green/10 rounded-lg p-4 border border-brand-green/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-5 h-5 text-brand-green" />
                      <span className="text-brand-green font-medium">Conseils</span>
                    </div>
                    <ul className="space-y-2">
                      {step.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                          <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <span className="text-gray-400 text-sm">
                  {currentStep + 1} / {tutorialSteps.length}
                </span>

                {currentStep < tutorialSteps.length - 1 ? (
                  <button
                    onClick={nextStep}
                    className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
                  >
                    Terminer
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with AI suggestion */}
        <div className="p-4 border-t border-gray-700 bg-brand-black/50">
          <div className="flex items-center gap-3 text-sm">
            <Bot className="w-5 h-5 text-blue-400" />
            <span className="text-gray-400">
              Need personalized help? Use the
              <button
                onClick={() => {
                  onClose();
                  // This will be handled by parent component
                }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Assistant IA
              </button>
              {' '}for advice tailored to your situation.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Welcome Banner Component
export const SessionsWelcomeBanner = ({ onStartTutorial, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed before
    const hasDismissed = localStorage.getItem('sessions_welcome_dismissed');
    if (hasDismissed) {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('sessions_welcome_dismissed', 'true');
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  if (dismissed) return null;

  return (
    <div className="bg-gradient-to-r from-brand-green/20 via-blue-500/10 to-purple-500/10 rounded-xl border border-brand-green/30 p-6 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-brand-green" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">
              Bienvenue dans les Sessions d'Exploitation!
            </h3>
            <p className="text-gray-300 mb-4 max-w-2xl">
              This page lets you manage your access to compromised systems.
              Learn how to use sessions, the AI Assistant, and the modules
              d'exploitation automatique pour optimiser votre pentest.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onStartTutorial}
                className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
              >
                <BookOpen className="w-4 h-4" />
                Suivre le tutoriel
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick start hints */}
      <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Brain className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="text-white font-medium text-sm">Assistant IA</h4>
            <p className="text-gray-400 text-xs">Posez vos questions sur l'exploitation</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20">
            <Rocket className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h4 className="text-white font-medium text-sm">PoC Auto</h4>
            <p className="text-gray-400 text-xs">Exploit vulnerabilities in 1 click</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-orange-500/20">
            <Shield className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h4 className="text-white font-medium text-sm">Actions Rapides</h4>
            <p className="text-gray-400 text-xs">Privesc, persistence, screenshots</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Floating Help Button
export const SessionsHelpButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 w-14 h-14 bg-brand-green text-brand-black rounded-full shadow-lg hover:bg-brand-green/90 transition-all hover:scale-110 flex items-center justify-center z-40 group"
      title="Aide et tutoriel"
    >
      <HelpCircle className="w-6 h-6" />
      <span className="absolute right-full mr-3 bg-brand-black-light text-white px-3 py-1.5 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700">
        Besoin d'aide?
      </span>
    </button>
  );
};

// Contextual Tooltip Component
export const ContextualTooltip = ({ id, title, content, position = 'bottom' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="p-1 text-gray-500 hover:text-brand-green transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {isVisible && (
        <div className={`absolute z-50 ${positionClasses[position]} w-64`}>
          <div className="bg-brand-black-light border border-brand-green/30 rounded-lg p-3 shadow-xl">
            {title && (
              <h4 className="text-brand-green font-medium text-sm mb-1">{title}</h4>
            )}
            <p className="text-gray-300 text-xs">{content}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsGuide;
